import { ComponentProps, useMemo } from 'react';

import * as SliderPrimitive from '@radix-ui/react-slider';

import { SLIDER_TEST_IDS } from '@/constants/data-testids';
import { cn } from '@/utils/tailwind.ts';

type SliderProps = {
  rangeClassName?: string;
  withoutPercentage?: boolean;
  thumbClassName?: string;
} & ComponentProps<typeof SliderPrimitive.Root>;

const Slider = ({
  className,
  withoutPercentage,
  defaultValue,
  rangeClassName,
  thumbClassName,
  value,
  min = 0,
  max = 100,
  ...props
}: SliderProps) => {
  const _values = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }

    if (Array.isArray(defaultValue)) {
      return defaultValue;
    }

    return [min, max];
  }, [value, defaultValue, min, max]);

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      data-testid={SLIDER_TEST_IDS.root}
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        'relative flex w-ful touch-none items-center select-none data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        data-testid={SLIDER_TEST_IDS.track}
        className={cn(
          'bg-custom-warm-grey-150 relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-3 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5'
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          data-testid={SLIDER_TEST_IDS.range}
          className={cn(
            'absolute bg-custom-warm-grey-400 data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
            rangeClassName
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => {
        const percentage = Math.round(((_values[index] - min) / (max - min)) * 100);
        return (
          <SliderPrimitive.Thumb
            key={index}
            data-slot="slider-thumb"
            data-testid={SLIDER_TEST_IDS.thumb(index)}
            className={cn(
              'relative w-2.5 h-6 bg-background ring-ring/50 block shrink-0 rounded-full transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none data-[disabled]:ring-0',
              thumbClassName
            )}
          >
            {!withoutPercentage && (
              <div className="absolute text-body-12-semibold bg-custom-warm-grey-50 px-2 py-1 outline outline-custom-warm-grey-200 rounded-sm -top-[29px] left-1/2 -translate-x-1/2 text-xs font-medium text-foreground">
                {percentage}%
              </div>
            )}
          </SliderPrimitive.Thumb>
        );
      })}
    </SliderPrimitive.Root>
  );
};

export { Slider };
